import { useEffect, useRef, useCallback } from 'react'

interface AiSimpleEditorProps {
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  className?: string
}

/**
 * Modern Rich Text Editor using Clipboard API
 *
 * Features:
 * - Uses modern Clipboard API with fallbacks to execCommand
 * - Supports rich content (HTML) copy/paste
 * - Secure clipboard operations with proper permissions
 * - Cross-browser compatibility
 *
 * Note: Clipboard read operations require user permission and HTTPS in production
 */

function AiSimpleEditor({ value = '', onChange, placeholder, className }: AiSimpleEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const isUnmountingRef = useRef(false)

  function sanitizeHtml(unsafeHtml: string): string {
    const doc = document.implementation.createHTMLDocument('sanitizer')
    doc.body.innerHTML = unsafeHtml || ''
    doc.body.querySelectorAll('script, style, iframe, object, embed').forEach((el) => el.remove())
    doc.body.querySelectorAll('*').forEach((el) => {
      ;[...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase()
        const value = (attr.value || '').trim().toLowerCase()
        const isEvent = name.startsWith('on')
        const isJsHref = name === 'href' && value.startsWith('javascript:')
        const isSrcDoc = name === 'srcdoc'
        if (isEvent || isJsHref || isSrcDoc) (el as HTMLElement).removeAttribute(attr.name)
      })
    })
    return doc.body.innerHTML
  }

  useEffect(() => {
    if (!editorRef.current || isUnmountingRef.current) return

    const sanitized = sanitizeHtml(value || '')
    if (editorRef.current.innerHTML !== sanitized) {
      requestAnimationFrame(() => {
        if (editorRef.current && !isUnmountingRef.current) {
          // Temporarily disable ResizeObserver by hiding the element
          const element = editorRef.current
          const originalDisplay = element.style.display
          element.style.display = 'none'

          // Force layout calculation
          element.offsetHeight

          // Restore display and update content
          element.style.display = originalDisplay
          element.innerHTML = sanitized
        }
      })
    }
  }, [value])

  // Cleanup effect to prevent ResizeObserver issues
  useEffect(() => {
    const editorElement = editorRef.current

    return () => {
      // Mark component as unmounting to prevent further operations
      isUnmountingRef.current = true

      // Prevent ResizeObserver loop errors during unmount
      if (editorElement) {
        // Clear focus and any pending operations
        editorElement.blur()

        // Temporarily hide element to prevent ResizeObserver issues
        const originalDisplay = editorElement.style.display
        editorElement.style.display = 'none'

        // Clear any pending animation frames and force layout
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Double RAF to ensure all pending operations complete
            if (document.body) {
              document.body.offsetHeight // Force layout calculation
            }
            // Restore display after cleanup
            editorElement.style.display = originalDisplay
          })
        })
      }
    }
  }, [])

  const handleInput = useCallback(() => {
    if (!onChange || !editorRef.current || isUnmountingRef.current) return

    requestAnimationFrame(() => {
      if (!editorRef.current || isUnmountingRef.current) return
      const sanitized = sanitizeHtml(editorRef.current.innerHTML)
      onChange(sanitized)
    })
  }, [onChange])

  const exec = useCallback(
    (cmd: string, arg?: string) => {
      // Check if the command is supported before executing (MDN best practice)
      if (document.queryCommandSupported && !document.queryCommandSupported(cmd)) {
        console.warn(`Command "${cmd}" is not supported in this browser`)
        return false
      }

      try {
        // Use execCommand with proper error handling (MDN best practice)
        const success = document.execCommand(cmd, false, arg)
        if (success) {
          handleInput()
        }
        return success
      } catch (error) {
        console.error(`Error executing command "${cmd}":`, error)
        return false
      }
    },
    [handleInput],
  )

  // Add keyboard shortcut support after exec is defined
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!editorRef.current || !editorRef.current.contains(e.target as Node)) return

      // Only handle basic formatting shortcuts (MDN recommendation)
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault()
            exec('bold')
            break
          case 'i':
            e.preventDefault()
            exec('italic')
            break
          case 'u':
            e.preventDefault()
            exec('underline')
            break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [exec])

  function handleCreateLink() {
    const url = window.prompt('Enter URL') || ''
    if (!url) return

    // Ensure protocol (MDN best practice)
    const withProtocol = /^(https?:)?\/\//i.test(url) ? url : `https://${url}`

    // Use execCommand as recommended by MDN for contentEditable
    exec('createLink', withProtocol)
  }

  // Modern font size using CSS instead of deprecated HTML fontSize attribute
  function handleModernFontSize(size: string) {
    const selection = window.getSelection()
    if (!selection || !editorRef.current) return

    if (selection.rangeCount > 0 && selection.toString().length > 0) {
      try {
        // Use modern approach with CSS styling
        const range = selection.getRangeAt(0)
        const span = document.createElement('span')
        span.style.fontSize = `${size}px`

        // Wrap selection in span with font-size
        if (range.collapsed) {
          // If no selection, just insert a styled span for future typing
          span.textContent = '\u00A0' // Non-breaking space
          range.insertNode(span)
          range.selectNodeContents(span)
        } else {
          // Wrap existing selection
          range.surroundContents(span)
        }

        selection.removeAllRanges()
        selection.addRange(range)
        handleInput()
      } catch (error) {
        // Fallback to execCommand if modern approach fails
        console.warn('Modern font size failed, using fallback')
        exec('fontSize', Math.round(parseInt(size) / 4).toString()) // Convert px to fontSize scale (1-7)
      }
    } else {
      // Apply to current block if no selection
      const currentNode = selection.focusNode
      if (currentNode && editorRef.current.contains(currentNode)) {
        const targetElement =
          currentNode.nodeType === Node.TEXT_NODE
            ? currentNode.parentElement
            : (currentNode as HTMLElement)

        if (targetElement && editorRef.current.contains(targetElement)) {
          targetElement.style.fontSize = `${size}px`
          handleInput()
        }
      }
    }
  }

  function handleTextAlign(alignment: string) {
    exec('justify' + alignment.charAt(0).toUpperCase() + alignment.slice(1))
  }

  function handleFormat(format: string) {
    // Use execCommand for format changes (MDN best practice for contentEditable)
    exec('formatBlock', format)
  }

  return (
    <div className={`ai-simple-editor ${className || ''}`}>
      <div className='ai-simple-editor-toolbar'>
        {/* Slim Inline Toolbar */}
        <div className='toolbar-group'>
          <button
            type='button'
            onClick={() => exec('bold')}
            title='Bold (Ctrl+B)'
            className='format-btn'
          >
            𝐁
          </button>
          <button
            type='button'
            onClick={() => exec('italic')}
            title='Italic (Ctrl+I)'
            className='format-btn'
          >
            𝐼
          </button>
          <button
            type='button'
            onClick={() => exec('underline')}
            title='Underline (Ctrl+U)'
            className='format-btn'
          >
            U̲
          </button>
        </div>

        <div className='toolbar-group'>
          <select
            onChange={(e) => handleFormat(e.target.value)}
            title='Text Format'
            className='format-select'
            defaultValue='p'
          >
            <option value='p'>Body Text</option>
            <option value='h1'>Heading 1</option>
            <option value='h2'>Heading 2</option>
            <option value='h3'>Heading 3</option>
            <option value='h4'>Heading 4</option>
            <option value='blockquote'>Quote</option>
            <option value='pre'>Code Block</option>
          </select>
          <select
            onChange={(e) => handleModernFontSize(e.target.value)}
            title='Font Size'
            className='size-select'
            defaultValue='16'
          >
            <option value='12'>Small (12px)</option>
            <option value='14'>Body (14px)</option>
            <option value='16'>Normal (16px)</option>
            <option value='18'>Large (18px)</option>
            <option value='20'>XL (20px)</option>
            <option value='24'>XXL (24px)</option>
          </select>
        </div>

        <div className='toolbar-group'>
          <button
            type='button'
            onClick={() => exec('insertUnorderedList')}
            title='Bullet List'
            className='action-btn'
          >
            ●
          </button>
          <button
            type='button'
            onClick={() => exec('insertOrderedList')}
            title='Numbered List'
            className='action-btn'
          >
            ①
          </button>
        </div>

        <div className='toolbar-group'>
          <button
            type='button'
            onClick={() => handleTextAlign('left')}
            title='Align Left'
            className='align-btn'
          >
            ⬅
          </button>
          <button
            type='button'
            onClick={() => handleTextAlign('center')}
            title='Center'
            className='align-btn'
          >
            ⬛
          </button>
          <button
            type='button'
            onClick={() => handleTextAlign('right')}
            title='Align Right'
            className='align-btn'
          >
            ➡
          </button>
        </div>

        <div className='toolbar-group'>
          <button
            type='button'
            onClick={handleCreateLink}
            title='Insert Link'
            className='special-btn'
          >
            🔗
          </button>
          <button
            type='button'
            onClick={() => exec('insertHorizontalRule')}
            title='Horizontal Line'
            className='special-btn'
          >
            ──
          </button>
          <button
            type='button'
            onClick={() => exec('removeFormat')}
            title='Clear Formatting'
            className='clear-btn'
          >
            ✕
          </button>
        </div>
      </div>
      <div
        ref={editorRef}
        className='ai-simple-editor-area'
        contentEditable
        role='textbox'
        aria-multiline='true'
        onInput={handleInput}
        data-placeholder={placeholder}
        style={{
          minHeight: 140,
          borderRadius: 6,
          padding: 6,
          lineHeight: 1.4,
          fontSize: 14,
          outline: 'none',
          background: '#fff',
        }}
      />
      <style>
        {`
        .ai-simple-editor-toolbar { 
          display: flex; 
          gap: 6px;
          align-items: center;
          margin-bottom: 0; 
          padding: 6px 8px; 
          background: #fafafa;       
          border-radius: 6px 6px 0 0;
          flex-wrap: wrap;
          min-height: 36px;
        }
        .toolbar-group {
          display: flex;
          gap: 1px;
          align-items: center;
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 3px;
          padding: 1px;
        }
        .ai-simple-editor-toolbar button {
          border: none;
          background: transparent; 
          padding: 4px 6px; 
          border-radius: 2px; 
          cursor: pointer;
          font-size: 13px;
          min-width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .ai-simple-editor-toolbar button:hover { 
          background: #f0f0f0; 
        }
        .ai-simple-editor-toolbar button:active { 
          background: #e6f7ff; 
        }
        /* Button Type Styles */
        .format-btn {
          font-weight: bold;
          font-size: 14px;
        }
        .action-btn, .align-btn, .indent-btn {
          font-size: 12px;
        }
        .special-btn {
          font-size: 11px;
        }
        .clear-btn {
          color: #ff4d4f;
          font-weight: bold;
          font-size: 12px;
        }
        .clear-btn:hover {
          background: #fff2f0;
        }
        .format-select, .size-select {
          border: none;
          background: transparent;
          padding: 2px 4px;
          border-radius: 2px;
          font-size: 11px;
          cursor: pointer;
          height: 22px;
        }
        .format-select {
          min-width: 65px;
        }
        .size-select {
          min-width: 55px;
        }
        .format-select:hover, .size-select:hover {
          background: #f0f0f0;
        }
        .ai-simple-editor-area {
          margin-top: 12px !important;
          border-radius: 0 0 6px 6px !important;
          background: #fff !important;
          padding: 12px 16px !important;
          border: 1px solid #e8e8e8;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
          caret-color: #1677ff;
        }
        .ai-simple-editor-area:hover {
          border-color: #bfbfbf;
        }
        .ai-simple-editor-area:focus {
          border-color: #91d5ff;
          box-shadow: 0 0 0 1px rgba(24, 144, 255, 0.2);
          background: #ffffff;
        }
        .ai-simple-editor-area:empty:before { 
          content: attr(data-placeholder); 
          color: #999;
          font-style: italic;
          pointer-events: none;
        }
        .ai-simple-editor-area a { 
          color: #1677ff; 
          text-decoration: underline; 
        }
        .ai-simple-editor-area ul { 
          padding-inline-start: 18px; 
          margin: 6px 0;
        }
        .ai-simple-editor-area ol { 
          padding-inline-start: 18px; 
          margin: 6px 0;
        }
        .ai-simple-editor-area h1 {
          font-size: 24px;
          font-weight: 700;
          line-height: 1.2;
          margin: 12px 0 10px 0;
          color: #1a1a1a;
        }
        .ai-simple-editor-area h2 {
          font-size: 20px;
          font-weight: 600;
          line-height: 1.3;
          margin: 10px 0 8px 0;
          color: #2a2a2a;
        }
        .ai-simple-editor-area h3 {
          font-size: 18px;
          font-weight: 600;
          line-height: 1.4;
          margin: 8px 0 6px 0;
          color: #3a3a3a;
        }
        .ai-simple-editor-area h4 {
          font-size: 16px;
          font-weight: 500;
          line-height: 1.4;
          margin: 6px 0 4px 0;
          color: #4a4a4a;
        }
        .ai-simple-editor-area p {
          font-size: 14px;
          line-height: 1.45;
          margin: 6px 0;
          color: #333;
        }
        .ai-simple-editor-area blockquote {
          border-left: 4px solid #1677ff;
          margin: 8px 0;
          padding: 8px 12px;
          background: #f8f9fa;
          font-style: italic;
          font-size: 14px;
          color: #555;
          border-radius: 0 4px 4px 0;
        }
        .ai-simple-editor-area pre {
          background: #f5f5f5;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 10px;
          margin: 6px 0;
          font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
          font-size: 13px;
          line-height: 1.4;
          overflow-x: auto;
          color: #333;
        }
        .ai-simple-editor-area hr {
          border: none;
          border-top: 2px solid #e0e0e0;
          margin: 12px 0;
          border-radius: 1px;
        }
      `}
      </style>
    </div>
  )
}

export { AiSimpleEditor }
